import { takeLatest, call, put, select, throttle, debounce, takeLeading } from 'redux-saga/effects';
import { head, omit, keys } from 'ramda';
import { string, number } from 'yup';
import { isEmail } from 'validator';

import { withAlert } from 'store/alerts';
import api from 'api';
import { getUserProp } from 'store/session/selectors';
import { EMAIL, INSTITUTION, ZIP_CODE } from 'store/patients';

import { EXPAND, RECEIVER, SUB_ORGANIZATION, SUB_PATIENT } from '.';
import { updateReports, updateReport, updatePagination } from './actions';
import {
  FETCH_REPORTS,
  FETCH_REPORTS_STATUS,
  FETCH_REPORT,
  SAVE_REPORT,
  SAVE_REPORT_DEBOUNCE,
  SAVE_EXTERN_REPORT,
  CREATE_REPORTS_ARCHIVE,
  UPLOAD_EXCEL_FILE,
} from './types';

function* fetchReports({ payload }) {
  const data = yield call(api.getReports, payload);
  yield put(updateReports(data.data));
  yield put(updatePagination(omit(['data'], data)));
}

function* fetchReport({ payload }) {
  return { success: yield call(api.getReport, payload, { [EXPAND]: [SUB_PATIENT] }) };
}

function* fetchReportsStatus() {
  return { success: yield call(api.getReportsStatus) };
}

const validateReceiverData = (data = {}) => {
  const isValidEmail = !data[EMAIL] || isEmail(data[EMAIL].trim());
  const isValidInstitution = !data[INSTITUTION] || data[INSTITUTION].length <= 45;
  const isValidZipCode =
    !data[ZIP_CODE] || (string().min(4).isValidSync(data[ZIP_CODE]) && number().positive('').isValidSync(data[ZIP_CODE]));
  const errors = {
    ...(!isValidEmail && { [EMAIL]: 'E-Mail-Adresse ungültig' }),
    ...(!isValidInstitution && {
      [INSTITUTION]: 'Der Name der Institution darf max. 45 Zeichen betragen. Der Wert wird nicht gespeichert.',
    }),
    ...(!isValidZipCode && { [ZIP_CODE]: 'PLZ ungültig' }),
  };

  return {
    errors,
    data: omit(keys(errors), data),
  };
};

function* saveReport({ payload }) {
  if (payload[RECEIVER]) {
    const { errors, data } = validateReceiverData(payload[RECEIVER]);

    return {
      success: yield call(api.patchReport, { ...payload, [RECEIVER]: data }, { [EXPAND]: [SUB_PATIENT] }),
      errors: { [RECEIVER]: errors },
    };
  }

  return { success: yield call(api.patchReport, payload, { [EXPAND]: [SUB_PATIENT] }) };
}

function* saveExternReport({ payload }) {
  const data = yield call(api.putReport, payload);
  yield put(updateReport(data));
}

function* createReportsArchive({ payload }) {
  yield call(api.postReportsArchive, payload);
}

function* uploadExcelFile({ payload }) {
  const orgId = yield select(getUserProp(SUB_ORGANIZATION));

  const { path } = yield call(api.uploadFile, head(payload));

  return yield call(api.postUploadResource, orgId, path);
}

export default function* watchReports() {
  yield throttle(500, FETCH_REPORTS, withAlert(fetchReports));
  yield takeLatest(FETCH_REPORTS_STATUS, withAlert(fetchReportsStatus));
  yield takeLatest(FETCH_REPORT, withAlert(fetchReport));
  yield takeLeading(SAVE_REPORT, withAlert(saveReport));
  yield debounce(500, SAVE_REPORT_DEBOUNCE, withAlert(saveReport));
  yield takeLatest(SAVE_EXTERN_REPORT, withAlert(saveExternReport));
  yield takeLatest(CREATE_REPORTS_ARCHIVE, withAlert(createReportsArchive));
  yield takeLatest(UPLOAD_EXCEL_FILE, withAlert(uploadExcelFile));
}
